import { Meta } from '@/components/seo/Meta';
import { useHomeSections, useSiteSettings } from '@/lib/cms';
import { siteSettingsDefault } from '@/lib/site-settings-default';
import type { HomeHero, HomeTrustedBy, HomeMission, HomeServicesPreview, HomeFeaturedProjects, HomeTechStack, HomeIndustries, HomeProcess, HomeTestimonials, HomeCtaBand, HomeLatestPosts } from '@/lib/cms-types';
import { Skeleton } from '@/components/ui/Skeleton';
import { HomeHero as HomeHeroSection } from '@/features/home/HomeHero';
import { HomeTrustedBy as HomeTrustedBySection } from '@/features/home/HomeTrustedBy';
import { HomeMission as HomeMissionSection } from '@/features/home/HomeMission';
import { HomeServicesPreview as HomeServicesPreviewSection } from '@/features/home/HomeServicesPreview';
import { HomeFeaturedProjects as HomeFeaturedProjectsSection } from '@/features/home/HomeFeaturedProjects';
import { HomeTechStack as HomeTechStackSection } from '@/features/home/HomeTechStack';
import { HomeIndustries as HomeIndustriesSection } from '@/features/home/HomeIndustries';
import { HomeProcess as HomeProcessSection } from '@/features/home/HomeProcess';
import { HomeTestimonials as HomeTestimonialsSection } from '@/features/home/HomeTestimonials';
import { HomeCtaBand as HomeCtaBandSection } from '@/features/home/HomeCtaBand';
import { HomeLatestPosts as HomeLatestPostsSection } from '@/features/home/HomeLatestPosts';

function asData<T>(record: Record<string, unknown>): T {
  return record as unknown as T;
}

/**
 * Home page — flagship. Reads home_sections and site_settings from Supabase
 * and renders each enabled section via its dedicated feature component.
 * Each section has safe defaults if the database is empty.
 */
export function HomePage() {
  const { data: settings } = useSiteSettings();
  const { data: sections = [], isLoading } = useHomeSections();

  const brandTagline = settings?.brand.tagline ?? siteSettingsDefault.brand.tagline;
  const description = settings?.seo.default_description ?? siteSettingsDefault.seo.default_description;

  if (isLoading) {
    return (
      <>
        <Meta title="Vonlof" description={description} path="/" />
        <div className="pt-24 sm:pt-28 md:pt-32">
          <Skeleton className="h-[60vh] w-full" />
        </div>
      </>
    );
  }

  return (
    <>
      <Meta title="Vonlof" description={description} path="/" />
      {sections.map((section) => {
        const d = asData<Record<string, unknown>>(section.data);
        switch (section.key) {
          case 'hero':
            return <HomeHeroSection key={section.id} data={asData<HomeHero>(d)} brandTagline={brandTagline} />;
          case 'trusted_by':
            return <HomeTrustedBySection key={section.id} {...asData<HomeTrustedBy>(d)} />;
          case 'mission':
            return <HomeMissionSection key={section.id} {...asData<HomeMission>(d)} />;
          case 'services_preview':
            return <HomeServicesPreviewSection key={section.id} {...asData<HomeServicesPreview>(d)} />;
          case 'featured_projects': {
            const data = asData<Partial<HomeFeaturedProjects>>(d);
            return (
              <HomeFeaturedProjectsSection
                key={section.id}
                headline={data.headline ?? 'Selected work'}
                view_all_label={data.view_all_label ?? 'All projects'}
                view_all_href={data.view_all_href ?? '/projects'}
                count={data.count ?? 3}
              />
            );
          }
          case 'tech_stack':
            return <HomeTechStackSection key={section.id} {...asData<HomeTechStack>(d)} />;
          case 'industries':
            return <HomeIndustriesSection key={section.id} {...asData<HomeIndustries>(d)} />;
          case 'process':
            return <HomeProcessSection key={section.id} {...asData<HomeProcess>(d)} />;
          case 'testimonials':
            return <HomeTestimonialsSection key={section.id} {...asData<HomeTestimonials>(d)} />;
          case 'cta_band':
            return <HomeCtaBandSection key={section.id} {...asData<HomeCtaBand>(d)} />;
          case 'latest_posts': {
            const data = asData<Partial<HomeLatestPosts>>(d);
            return (
              <HomeLatestPostsSection
                key={section.id}
                headline={data.headline ?? 'Insights'}
                view_all_label={data.view_all_label ?? 'All posts'}
                view_all_href={data.view_all_href ?? '/blog'}
                count={data.count ?? 3}
              />
            );
          }
          default:
            return null;
        }
      })}
    </>
  );
}